import { useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';

/**
 * Pre-call entitlement guards (react-query `enabled:` / page cards).
 *
 *   ready        - the company details are known (a guard must keep its queries
 *                  disabled until then, so no call fires before we know)
 *   has(key)     - may this company USE the feature? A MISSING key is allowed;
 *                  ONLY an explicit `false` blocks. Returns false while !ready.
 *                  Restaurant accounts: always true (the guards are inert for them).
 *   features     - package.features as sent by /company/details ({} when none)
 *   isRestaurant - company.account_type === 'restaurant'
 *
 * This is deliberately NOT the same rule as FeatureGate / useHasFeature, which
 * stay strict (`=== true`) and keep deciding what a page SHOWS. This hook only
 * decides whether a gated API call is worth making at all: the backend answers
 * a package that lacks the feature with a 402, so an explicit `false` is the
 * one case where the call is certain to be refused.
 *
 * Source: the 'getUserSubscription' react-query cache first (it holds the
 * /company/details the router waited for, so it is there from the first
 * render), then redux companyDetails (same precedent as usePlanState).
 */
export function useEntitlements() {
  const qc = useQueryClient();
  const redux = useSelector((s) => s.global?.companyDetails);
  const details = qc.getQueryData('getUserSubscription') ?? redux ?? null;

  const ready = !!details && typeof details === 'object';
  const features = (ready && details.package?.features) || {};
  const isRestaurant = ready && details.company?.account_type === 'restaurant';

  const has = (key) => {
    if (!ready) return false;
    if (isRestaurant) return true;
    return features[key] !== false;
  };

  return { ready, has, features, isRestaurant };
}

export default useEntitlements;
